import React from 'react';
import styled from 'styled-components/native';
import {useTheme} from 'styled-components';
import {wdp} from '../../utils/responsive';
import Icon from '../icon';
import {Theme} from '../../theme/lightTheme';
import {tr} from '../../translations';
import {Pressable} from 'react-native';
import {SearchBarProps} from '../../interfaces/components/searchBar';
import GenericInput from '../textInput';

const Container = styled(Pressable)`
  width: 100%;
  align-items: center;
  border-radius: 6px;
  flex-direction: row;
  background-color: ${({theme}) => theme.homeBackground};
  overflow: hidden;
`;
const ContentContainer = styled.View`
  width: 100%;
  align-items: center;
  border-radius: 6px;
  flex-direction: row;
  background-color: ${({theme}) => theme.homeBackground};
  overflow: hidden;
`;
const Input = styled(GenericInput)``;
/**
 * `SearchBar` is a React component representing a search input bar with an optional action icon.
 * It provides a flexible and stylized input field for users to enter search queries.
 *
 * @component
 *
 * @param {Object} props - The properties of the `SearchBar` component.
 * @param {number} [props.iconSize] - The size of the optional action icon.
 * @param {Object} [props.iconStyle] - Additional styles to apply to the action icon.
 * @param {string} [props.iconName] - The name of the optional action icon to be displayed.
 * @param {string} [props.iconType] - The type of the optional action icon (e.g., 'Ionicons').
 * @param {string} [props.iconColor] - The color of the optional action icon.
 * @param {() => void} [props.onIconPress] - A callback function to be executed when the action icon is pressed.
 * @param {Object} [props.textInputStyle] - Additional styles to apply to the search input field.
 * @param {Object} [props.style] - Additional styles to apply to the entire `SearchBar` component.
 * @param {...any} [props] - Additional props to be passed down to the underlying TextInput component.
 *
 * @returns {React.ReactElement} A React functional component representing the search input bar.
 */
const SearchBar = ({
  iconSize,
  iconStyle,
  iconName,
  iconType,
  iconColor,
  onIconPress,
  textInputStyle,
  onPress,
  style,
  pointerEvents,
  ...props
}: SearchBarProps): React.ReactElement => {
  // Use the `useTheme` hook to get the theme object and cast it to the `Theme` type
  const theme = useTheme() as Theme;

  return (
    <Container
      pointerEvents={pointerEvents}
      disabled={!onPress}
      onPress={onPress}
      style={style}>
      <ContentContainer pointerEvents={onPress ? 'none' : 'auto'}>
        <Input
          returnKeyType={'search'}
          style={[{padding: theme.text.s10, borderWidth: 0}, textInputStyle]}
          placeholder={tr('search.placeholder')}
          {...props}
          hideFocusedEffect
        />
        {iconName && iconType && (
          <Icon
            name={iconName || 'record'}
            type={iconType || 'SVG'}
            color={iconColor || theme.border}
            size={iconSize ? iconSize : theme.text.s6}
            style={[iconStyle, {paddingHorizontal: wdp(onIconPress ? 2 : 4.5)}]}
            onPress={onIconPress}
          />
        )}
      </ContentContainer>
    </Container>
  );
};
SearchBar.defaultProps = {
  style: {},
  iconType: 'SVG',
};
export default SearchBar;
